/**
 * Camera-occlusion fade.
 *
 * User report (2026-08-24): "all my teammates keep running into me, and
 * because of this I cannot see anything around me — their models and
 * textures block my view."
 *
 * `sim/formation.ts` on the server stops the party converging on the player,
 * which is the actual fix. This is the second half: whatever *does* end up
 * between the camera and the player — a squadmate squeezed there by a
 * corridor, a monster that walked around behind you — stops being an opaque
 * wall of texture.
 *
 * **Measured, not derived.** The first version of this file used a fixed
 * radius (2.4m) reasoned out from `BOOM_LENGTH_M` being 3.8m. Live
 * instrumentation showed that was useless: the local player's own model sat
 * 4.35m from the eye and squadmates sat at 2.7m-3.8m, so every one of them
 * was directly between the camera and the player and every one of them was
 * at full opacity. The boom also collapses against geometry, so no fixed
 * radius can be right at every moment.
 *
 * The rule is relative instead: a character is occluding precisely when it is
 * *nearer the camera than your own character is*, and the fade is driven by
 * how far in front it has got — in metres, not as a fraction of the boom.
 * This gets the two hard cases right for free:
 *
 * - The local player never fades, because its distance IS the reference.
 * - A monster you are fighting never fades, because melee happens in front of
 *   your character and therefore further from the camera. Only one that has
 *   circled behind you does — which is what you want, since you cannot parry
 *   something behind you but you do need to see past it.
 *
 * Never reaches zero. A squadmate you cannot see at all is worse than one you
 * can see through, and a monster's health bar needs a body to hang off.
 */

/**
 * How far in front of the player a character may be before it fades at all.
 * Small: a squadmate standing shoulder to shoulder with you is not blocking
 * anything, and one a hand's breadth nearer the lens has already started to.
 */
export const OPAQUE_LEAD_M = 0.15;

/** How far in front of the player a character is fully faded. */
export const FULL_FADE_LEAD_M = 0.5;

export const MIN_ALPHA = 0.1;

/**
 * Opacity for a character `distanceM` from the camera eye, given the local
 * player is `playerDistanceM` from it.
 *
 * Keyed on the *lead* — how much nearer the lens this character is than your
 * own — rather than on any fraction of the boom. The second version of this
 * file used `playerDistanceM * 0.5` as the fade floor, and live measurement
 * killed that too: with the boom collapsed against geometry to 3.06m, a
 * squadmate at 2.72m filling a third of the screen scored an alpha of exactly
 * 1.00, because 2.72 still sat above the 2.56m threshold that formula
 * produced. Scaling the *response* with the boom was wrong — the boom changes
 * constantly, and 0.34m in front of you is 0.34m in front of you at any boom
 * length.
 *
 * `playerDistanceM <= 0` means the local player's model has not been placed
 * yet (still loading); everything stays opaque rather than flashing
 * translucent for a frame.
 *
 * Linear, not eased: the band is only 0.35m wide, and easing inside it just
 * moves where the visible step is.
 */
export function cameraOcclusionAlpha(distanceM: number, playerDistanceM: number): number {
  if (playerDistanceM <= 0) return 1;
  const lead = playerDistanceM - distanceM;
  if (lead <= OPAQUE_LEAD_M) return 1;
  if (lead >= FULL_FADE_LEAD_M) return MIN_ALPHA;
  const t = 1 - (lead - OPAQUE_LEAD_M) / (FULL_FADE_LEAD_M - OPAQUE_LEAD_M);
  return MIN_ALPHA + (1 - MIN_ALPHA) * t;
}

/**
 * The camera boom length at which the local player's own body starts fading,
 * and the length at which it is fully gone.
 *
 * User report (2026-08-25): "during combat, the camera occasionally gets
 * blocked by textures and similar objects." Measured cause: the boom's
 * collision floor (`MIN_CAMERA_DISTANCE`) overrode the wall hit, so when
 * geometry was nearer than the floor the eye was placed *inside* it. Over a
 * 2.2s forward walk, 55 of 245 frames had geometry closer than the floor, and
 * the worst frame had a wall 1.30m away with the boom pinned at 2.00m — the
 * eye 0.70m through the wall, looking at its interior.
 *
 * The floor existed for a real reason: at 0.5m the eye sat inside the
 * character's own head mesh, which fills the screen with a featureless
 * close-up. So the floor is gone and this replaces it. Letting the wall win
 * and fading your own body is strictly better than punching through the wall:
 * a near-first-person shot when you are jammed into a corner still shows you
 * the room, and the interior of a wall shows you nothing.
 *
 * Distinct from `cameraOcclusionAlpha` above, which cannot express this: that
 * function measures a character against the player's own distance, so for the
 * local player the lead is always exactly 0 and the alpha always exactly 1.
 * This one is keyed on the boom length instead.
 *
 * Both values are well above the near clip plane (0.1m) on purpose — the body
 * must be gone before it can be clipped through, not at the same moment. The
 * band sits below the boom lengths ordinary play produces (measured: 2.49m
 * backing up, 3.8m in the open) so a normal shot is never ghosted.
 */
export const SELF_OPAQUE_M = 2.0;
export const SELF_FULL_FADE_M = 1.0;

/**
 * Opacity for the local player's own model, given the camera's current
 * **boom length** — the eye's distance from the shoulder anchor. Fully
 * transparent when the boom is short, so your own body never becomes the thing
 * blocking the view.
 *
 * Takes the boom, NOT the distance from the eye to the model, and that
 * distinction was a bug first: a CharacterVisual's `root` sits at the feet, so
 * with the eye at shoulder height a fully collapsed 0.5m boom still measures
 * ~1.68m to the root. Measured live — the body stayed at alpha 1.00 and filled
 * roughly 40% of the frame with the boom collapsed to its floor.
 *
 * Reaches 0 rather than `MIN_ALPHA`: the reasoning for never fully hiding
 * another character — you still need to see where it is, and a health bar
 * needs a body — does not apply to your own body, which you cannot lose track
 * of and whose position the camera itself is reporting.
 */
export function localPlayerFadeAlpha(boomLengthM: number): number {
  if (boomLengthM >= SELF_OPAQUE_M) return 1;
  if (boomLengthM <= SELF_FULL_FADE_M) return 0;
  return (boomLengthM - SELF_FULL_FADE_M) / (SELF_OPAQUE_M - SELF_FULL_FADE_M);
}
